import React from 'react';
import { getHoursMinutes } from 'pages/LearningResources/utils';
import { LearningTracks } from 'types/LearningResources/types';
import {
  Checkmark,
  LockClosed,
  LockOpen
} from '@teachstonellc/component-library/dist/icons';

interface PlaylistCardProps {
  isIntro: boolean;
  introImage: string | undefined;
  track: LearningTracks;
  isActive?: boolean;
  onSelect?: (track: LearningTracks) => void;
  classVersionsTags?: any[];
  ageLevelsTags?: any[];
  isOpen?: boolean;
  fallbackImage?: string;
}

const tagClasses =
  'rounded-3xl border border-ds-blue-7 text-ds-gray-9 text-center font-medium font-inter text-md leading-4 px-3 py-1 w-fit gap-1';

function renderClassVersions(id: number, classVersionsTags: any[]) {
  return (
    <div className="text-ds-cool-gray-7 font-medium text-md leading-4 mr-2">
      {classVersionsTags
        .filter((tag: any) => tag.learningTrackID === id)
        .map((tag: any) => tag.description)
        .slice(0, 1)}
    </div>
  );
}

function renderAgeLevels(
  id: number,
  ageLevelsTags: any[],
  classVersionsTags: any[]
) {
  const tags = ageLevelsTags
    .filter((tag: any) => tag.learningTrackID === id)
    .map((tag: any) => tag.description)
    .slice(0, 1);

  if (tags.length === 0) return null;

  return (
    <span
      className={`${classVersionsTags.length === 0 ? 'ml-4' : ''} ${tagClasses} mr-2`}
    >
      {tags}
    </span>
  );
}

const PlaylistCard: React.FC<PlaylistCardProps> = ({
  isIntro,
  introImage,
  track,
  isActive = false,
  isOpen = false,
  onSelect,
  classVersionsTags = [],
  ageLevelsTags = [],
  fallbackImage
}) => {
  const { name, video_uri, estimated_time, resource_type, thumbnail_url } =
    track.learning_resources;
  return (
    <button
      className={`w-full bg-white rounded-xl h-fit cursor-pointer mt-4 ${
        isActive ? 'border-2 border-ds-fuschia-9' : ''
      } ${!isOpen ? 'opacity-50 pointer-events-none' : ''}`}
      style={{ boxShadow: '0px 22px 40px 0px #0000000D' }}
      onClick={() => isOpen && onSelect && onSelect(track)}
      key={track.id}
    >
      <div
        className={`grid grid-cols-3 mb-4 sm:mb-0 ${
          isIntro ? 'sm:h-[60px]' : 'sm:h-[189px]'
        }`}
      >
        <div className="col-span-3 sm:col-span-1 relative">
          <img
            className={`w-full max-w-full rounded-s-md object-cover ${
              isIntro ? 'sm:h-[60px]' : 'sm:h-[189px]'
            }`}
            src={
              isIntro
                ? introImage
                : resource_type === 'non_video'
                  ? thumbnail_url || fallbackImage
                  : `https://cdnsecakmi.kaltura.com/p/966871/thumbnail/entry_id/${video_uri}/width/600`
            }
            alt="Thumbnail"
          />
          {!isIntro && (
            <div className="mt-2 ml-2 absolute top-0 left-0 bg-white text-black px-2 py-2 rounded-3xl text-sm font-semibold">
              {getHoursMinutes(estimated_time)}
            </div>
          )}
        </div>

        <div className="col-span-3 sm:col-span-2 self-center p-4">
          <div className="w-full">
            {!isIntro && (
              <div className="flex items-center gap-1 text-md font-medium mb-2">
                {track.completed_at ? (
                  <span className="inline-flex items-center font-semibold text-ds-green-10 text-md leading-4">
                    <Checkmark size={20} className="text-ds-icon-positive" />
                    <span>Completed</span>
                  </span>
                ) : isOpen ? (
                  <>
                    <LockOpen size={16} className="text-ds-fuschia-9" />
                    <p className=" text-ds-fuschia-10">Open</p>
                  </>
                ) : (
                  <>
                    <LockClosed size={16} className="text-ds-icon-subtle" />
                    <p className="text-ds-cool-gray-7">Locked</p>
                  </>
                )}
              </div>
            )}
            <h4 className="text-lg font-semibold text-ds-gray-9 leading-5 text-left">
              {isIntro ? 'Introduction' : `${name}`}
            </h4>

            {/* Tags */}
            {!isIntro && (
              <div className={'flex items-center h-[22px] mt-2 py-2 mb-2'}>
                {classVersionsTags &&
                  renderClassVersions(track.id, classVersionsTags)}
                {ageLevelsTags &&
                  renderAgeLevels(track.id, ageLevelsTags, classVersionsTags)}
              </div>
            )}
          </div>
        </div>
      </div>
    </button>
  );
};

export default PlaylistCard;
